/**
 * O push com o CRM fechado (#536, ADR-0017): a chave pública do servidor e a
 * inscrição do navegador.
 *
 * ── A ordem, do lado do navegador ────────────────────────────────────────
 *
 *   1. `GET /v1/push/chave-publica` — a chave VAPID pública, que o navegador
 *      exige para criar a inscrição. `null` quando o servidor não tem push
 *      configurado: a tela esconde a opção em vez de oferecer o que não vai
 *      funcionar.
 *   2. `pushManager.subscribe(...)` no navegador — devolve o `endpoint` e as
 *      duas chaves.
 *   3. `POST /v1/me/dispositivos` — guarda a inscrição, ligada a quem está
 *      logado nesta revenda.
 *
 * Desligar, ou sair do CRM, é `POST /v1/me/dispositivos/remover`.
 */

import { z } from 'zod';
import { IdSchema } from './comum.js';

export const ChavePublicaDoPushSchema = z
  .object({
    /** Chave VAPID pública, em base64url. `null`: push não configurado neste servidor. */
    chave_publica: z.string().nullable(),
  })
  .strict();

export type ChavePublicaDoPush = z.infer<typeof ChavePublicaDoPushSchema>;

/**
 * A inscrição que o navegador entrega (`PushSubscription.toJSON()`).
 *
 * `endpoint` passa por uma segunda régua no servidor: só serviço de push
 * conhecido, em https. Quem manda para lá é o NOSSO servidor, e um endpoint
 * livre transformaria o despachante numa porta para a rede interna.
 */
export const RegistrarDispositivoSchema = z
  .object({
    plataforma: z.literal('web'),
    endpoint: z.string().url().max(1024),
    chaves: z
      .object({
        p256dh: z.string().min(1).max(256),
        auth: z.string().min(1).max(64),
      })
      .strict(),
    /** "Chrome no macOS" — para a pessoa reconhecer o aparelho um dia. */
    rotulo: z.string().trim().max(120).optional(),
  })
  .strict();

export type RegistrarDispositivo = z.infer<typeof RegistrarDispositivoSchema>;

export const DispositivoRegistradoSchema = z.object({ id: IdSchema }).strict();

export type DispositivoRegistrado = z.infer<typeof DispositivoRegistradoSchema>;

/** Pelo endpoint, que é o que o navegador sabe de si — o id é nosso. */
export const RemoverDispositivoSchema = z.object({ endpoint: z.string().url().max(1024) }).strict();

export type RemoverDispositivo = z.infer<typeof RemoverDispositivoSchema>;

/** Quantas linhas saíram — zero numa repetição, ou para endpoint de outra pessoa. */
export const DispositivosRemovidosSchema = z
  .object({ removidos: z.number().int().min(0) })
  .strict();

export type DispositivosRemovidos = z.infer<typeof DispositivosRemovidosSchema>;
